/**
 * MaintenancePage — full-site "Coming Soon / Under Maintenance" screen.
 *
 * Rendered by `App.tsx` in place of the routed page tree whenever
 * `SiteSettings.maintenance.enabled` is true. This is the headless-native
 * replacement for a traditional WordPress maintenance plugin — those
 * plugins hook `template_redirect` / `wp_head`, neither of which ever
 * fires for visitors on this stack (see `class-cors.php` and
 * `headless-placeholder/index.php` for the same architectural note).
 * Instead the toggle lives in WP Admin → Settings → Headless Bridge →
 * Maintenance Mode, is read via the `/settings` REST endpoint, and this
 * component is what actually renders when it's on.
 *
 * wp-admin itself is never gated — Apache only proxies `/wp-*` paths to
 * WordPress directly, so editors can always log in and flip the toggle
 * back off regardless of what the SPA is showing visitors.
 */
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';

interface MaintenancePageProps {
  message?: string;
}

export function MaintenancePage({ message }: MaintenancePageProps): JSX.Element {
  return (
    <>
      <Helmet>
        <title>We'll Be Back Soon | Operisoft</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="relative min-h-screen flex flex-col items-center justify-center px-6 py-16 bg-white dark:bg-slate-950 overflow-hidden">
        {/* Subtle background grid, matches the rest of the site's light sections */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.02)_1px,transparent_1px)] bg-[size:48px_48px] dark:bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)]" />

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative z-10 max-w-lg mx-auto text-center"
        >
          {/* Simple animated wrench/gear mark instead of a stock illustration */}
          <div className="mx-auto mb-8 w-16 h-16 rounded-2xl flex items-center justify-center bg-gradient-to-br from-blue-500 to-cyan-500 shadow-lg shadow-blue-500/25">
            <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M11 4.5a3.5 3.5 0 10.464 6.965l6.36 6.36a1.5 1.5 0 002.121-2.122l-6.36-6.36A3.5 3.5 0 0011 4.5z" />
            </svg>
          </div>

          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight">
            We'll be back{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500">
              soon
            </span>
          </h1>

          <p className="mt-5 text-base sm:text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
            {message && message.trim() !== ''
              ? message
              : "We're making some improvements to the site. We won't be long — thanks for your patience."}
          </p>

          <div className="mt-8 flex items-center justify-center gap-2 text-sm text-slate-400 dark:text-slate-500">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-400 opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-cyan-400" />
            </span>
            Maintenance in progress
          </div>

          <p className="mt-10 text-sm text-slate-500 dark:text-slate-400">
            Need something urgently?{' '}
            <a href="mailto:info@operisoft.com" className="font-medium text-blue-600 dark:text-blue-400 hover:underline">
              info@operisoft.com
            </a>
          </p>
        </motion.div>
      </div>
    </>
  );
}

export default MaintenancePage;
